import { ReloadOutlined, SendOutlined } from '@ant-design/icons';
import type { ServiceMessageRow } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { formatDisplayTime } from '@hq/utils';
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, App, Badge, Button, Card, Col, Empty, Input, Row, Space, Spin, Statistic, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { hq, useAuthStore } from '../session';

/** 每次拉取的消息条数（按 threadNo 聚合为会话列表，可「加载更早会话」续拉）。 */
const PAGE_SIZE = 50;
/** 单会话消息上限：一个会话的往来通常远小于此。 */
const THREAD_PAGE_SIZE = 200;

const SESSION_TYPE_TEXT: Record<string, string> = {
  WORKORDER: '工单会话',
  SATISFACTION: '评价回复',
  CONSULT: '在线答疑'
};

interface ThreadSummary {
  threadNo: string;
  sessionType: string;
  bizType: string | null;
  bizNo: string | null;
  lastContent: string | null;
  lastAt: string;
  unread: number;
  overtime: boolean;
}

/**
 * 客服中心：服务会话工作台。
 * 会话与消息来自 `sv_message`（docs/03 §K3），首次客服回复自动落 `response_minutes`/`is_overtime`，
 * 顶部统计即「24h 响应达标率」的举证口径（★2(3) 实时售后回复）。
 */
export default function ServiceCenter() {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const profile = useAuthStore((state) => state.profile);

  // 支持深链直接打开会话：`/service?thread=SV_SATISFACTION-xxx`（满意度评价页跳转）
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeThread, setActiveThread] = useState<string | null>(searchParams.get('thread'));
  const [draft, setDraft] = useState('');

  const openThread = (threadNo: string) => {
    setActiveThread(threadNo);
    const next = new URLSearchParams(searchParams);
    next.set('thread', threadNo);
    setSearchParams(next, { replace: true });
  };

  // 最近消息（升序）：游标续拉（后端返回 hasMore/nextLastId），前端按 threadNo 聚合出会话列表
  const inbox = useInfiniteQuery({
    queryKey: ['ops', 'service', 'inbox'],
    placeholderData: keepPreviousData,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => hq.message.list({ lastId: pageParam ?? undefined, size: PAGE_SIZE }),
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextLastId : undefined)
  });

  const thread = useQuery({
    queryKey: ['ops', 'service', 'thread', activeThread],
    placeholderData: keepPreviousData,
    queryFn: () => hq.message.list({ threadNo: activeThread ?? '', size: THREAD_PAGE_SIZE }),
    enabled: Boolean(activeThread)
  });

  const stats = useQuery({
    queryKey: ['ops', 'service', 'stats'],
    placeholderData: keepPreviousData,
    queryFn: () => hq.message.stats()
  });

  const read = useMutation({
    mutationFn: (threadNo: string) => hq.message.read(threadNo),
    // 标已读后必须让会话列表失效：否则列表里的 unread 一直是旧值，
    // 下面的自动标已读 effect 会反复判定"仍有未读"而持续发请求。
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['ops', 'service'] });
    }
  });

  const send = useMutation({
    mutationFn: (content: string) =>
      hq.message.send({
        threadNo: activeThread ?? undefined,
        sessionType: (threads.find((item) => item.threadNo === activeThread)?.sessionType ?? 'CONSULT') as
          | 'WORKORDER'
          | 'SATISFACTION'
          | 'CONSULT',
        bizType: threads.find((item) => item.threadNo === activeThread)?.bizType ?? undefined,
        bizNo: threads.find((item) => item.threadNo === activeThread)?.bizNo ?? undefined,
        senderType: 'STAFF',
        receiverUserId: undefined,
        channel: 'INNER',
        contentType: 'TEXT',
        content
      }),
    onSuccess: async () => {
      setDraft('');
      await queryClient.invalidateQueries({ queryKey: ['ops', 'service'] });
    },
    onError: (error) => {
      message.error(error instanceof ApiError ? error.friendlyMessage : '发送失败');
    }
  });

  const items = inbox.data?.pages.flatMap((page) => page.items) ?? [];

  const threads = useMemo<ThreadSummary[]>(() => {
    const map = new Map<string, ThreadSummary>();
    for (const row of items) {
      const unread = row.readAt === null && row.senderUserId !== profile?.userId ? 1 : 0;
      const current = map.get(row.threadNo);
      if (!current) {
        map.set(row.threadNo, {
          threadNo: row.threadNo,
          sessionType: row.sessionType,
          bizType: row.bizType,
          bizNo: row.bizNo,
          lastContent: row.content,
          lastAt: row.sendAt,
          unread,
          overtime: row.isOvertime
        });
        continue;
      }
      current.unread += unread;
      if (dayjs(row.sendAt).isAfter(dayjs(current.lastAt))) {
        current.lastContent = row.content;
        current.lastAt = row.sendAt;
        current.sessionType = row.sessionType;
        current.bizType = row.bizType;
        current.bizNo = row.bizNo;
      }
      current.overtime = current.overtime || row.isOvertime;
    }
    return [...map.values()].sort((a, b) => (dayjs(a.lastAt).isBefore(dayjs(b.lastAt)) ? 1 : -1));
  }, [items, profile?.userId]);

  // 打开会话即有未读则标记已读。
  // 依赖里放 mutation 对象（`read`）会让它每次渲染都是新引用 → effect 每渲染都跑 →
  // 打开一个未读会话就是一串并发标已读请求。改用 ref 记录"已经为哪个会话发过"，
  // 每个会话只发一次。
  const markedReadRef = useRef<string | null>(null);
  // mutation 对象每次渲染都是新引用，放进依赖会让 effect 每渲染重跑；用 ref 持有最新实例
  const readRef = useRef(read);
  readRef.current = read;
  useEffect(() => {
    if (!activeThread) {
      markedReadRef.current = null;
      return;
    }
    const target = threads.find((item) => item.threadNo === activeThread);
    if (!target || target.unread <= 0) return;
    if (markedReadRef.current === activeThread) return;

    markedReadRef.current = activeThread;
    readRef.current.mutate(activeThread);
  }, [activeThread, threads]);

  const messages = thread.data?.items ?? [];
  const onTimeRate = stats.data ? Number(stats.data.onTimeRate) : 0;

  const renderBubble = (row: ServiceMessageRow) => {
    const mine = row.senderType === 'STAFF';
    return (
      <div key={row.id} style={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start', marginBottom: 12 }}>
        <div style={{ maxWidth: '72%' }}>
          <Space size={6} style={{ marginBottom: 2 }}>
            <Typography.Text type="secondary">
              {row.senderName ?? (mine ? '客服' : '采购人')} · 第 {row.turnNo} 轮
            </Typography.Text>
            <Typography.Text type="secondary">{formatDisplayTime(row.sendAt)}</Typography.Text>
            {row.firstReplyFlag ? (
              <Tag color={row.isOvertime ? 'red' : 'green'}>
                首次响应 {row.responseMinutes} 分钟{row.isOvertime ? '（超时）' : ''}
              </Tag>
            ) : null}
          </Space>
          <div
            style={{
              background: mine ? 'var(--hq-color-primary, #88157A)' : '#f5f5f5',
              color: mine ? '#fff' : 'rgba(0,0,0,0.88)',
              padding: '8px 12px',
              borderRadius: 8,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word'
            }}
          >
            {row.content ?? `[${row.contentType}]`}
          </div>
        </div>
      </div>
    );
  };

  return (
    <Card
      className="page-card"
      title="客服中心"
      extra={
        <Button
          icon={<ReloadOutlined />}
          loading={inbox.isFetching || thread.isFetching}
          onClick={() => {
            void inbox.refetch();
            if (activeThread) void thread.refetch();
            void stats.refetch();
          }}
        >
          刷新
        </Button>
      }
    >
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Statistic title="会话数（已有响应）" value={stats.data?.repliedCount ?? 0} />
        </Col>
        <Col span={6}>
          <Statistic
            title="24h 响应达标率"
            value={onTimeRate * 100}
            precision={1}
            suffix="%"
            valueStyle={{ color: onTimeRate >= 0.95 ? '#3f8600' : '#cf1322' }}
          />
        </Col>
        <Col span={6}>
          <Statistic title="平均首次响应（分钟）" value={Math.round(Number(stats.data?.avgResponseMinutes ?? 0))} />
        </Col>
        <Col span={6}>
          <Statistic
            title="超时会话"
            value={stats.data?.overtimeCount ?? 0}
            valueStyle={{ color: (stats.data?.overtimeCount ?? 0) > 0 ? '#cf1322' : undefined }}
          />
        </Col>
      </Row>

      {inbox.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="会话列表加载失败"
          description={inbox.error instanceof ApiError ? inbox.error.friendlyMessage : String(inbox.error)}
        />
      ) : null}

      <Row gutter={16}>
        <Col span={8}>
          <div style={{ border: '1px solid #f0f0f0', borderRadius: 8, height: 560, overflowY: 'auto' }}>
            {inbox.isLoading ? (
              <div style={{ padding: 24, textAlign: 'center' }}>
                <Spin />
              </div>
            ) : threads.length === 0 ? (
              <Empty description="暂无售后会话" style={{ marginTop: 80 }} />
            ) : (
              threads.map((item) => {
                const active = item.threadNo === activeThread;
                return (
                  <div
                    key={item.threadNo}
                    onClick={() => openThread(item.threadNo)}
                    style={{
                      padding: '10px 12px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #f5f5f5',
                      background: active ? 'var(--hq-color-primary-bg, #f7ecf6)' : undefined
                    }}
                  >
                    <Space size={6} style={{ width: '100%', justifyContent: 'space-between' }}>
                      <Space size={6}>
                        <Tag color="blue">{SESSION_TYPE_TEXT[item.sessionType] ?? item.sessionType}</Tag>
                        {item.overtime ? <Tag color="red">超时</Tag> : null}
                      </Space>
                      <Space size={6}>
                        <Typography.Text type="secondary">{formatDisplayTime(item.lastAt)}</Typography.Text>
                        {item.unread > 0 ? <Badge count={item.unread} size="small" /> : null}
                      </Space>
                    </Space>
                    <div style={{ marginTop: 4 }}>
                      <Typography.Text strong ellipsis style={{ maxWidth: 220 }}>
                        {item.bizNo ?? item.threadNo}
                      </Typography.Text>
                    </div>
                    <Typography.Text type="secondary" ellipsis style={{ maxWidth: 280, display: 'block' }}>
                      {item.lastContent ?? '[附件]'}
                    </Typography.Text>
                  </div>
                );
              })
            )}
            {inbox.hasNextPage ? (
              <div style={{ padding: 12, textAlign: 'center' }}>
                <Button size="small" loading={inbox.isFetchingNextPage} onClick={() => inbox.fetchNextPage()}>
                  加载更早会话
                </Button>
              </div>
            ) : null}
          </div>
        </Col>

        <Col span={16}>
          <div
            style={{
              border: '1px solid #f0f0f0',
              borderRadius: 8,
              height: 560,
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {!activeThread ? (
              <Empty description="选择左侧会话查看对话" style={{ marginTop: 160 }} />
            ) : (
              <>
                <div style={{ padding: '8px 12px', borderBottom: '1px solid #f0f0f0' }}>
                  <Space>
                    <Typography.Text strong>{activeThread}</Typography.Text>
                    <Button type="link" size="small" onClick={() => read.mutate(activeThread)}>
                      标记已读
                    </Button>
                  </Space>
                </div>
                <div style={{ flex: 1, overflowY: 'auto', padding: 12 }}>
                  {thread.isLoading ? <Spin /> : messages.map(renderBubble)}
                </div>
                <div style={{ borderTop: '1px solid #f0f0f0', padding: 12 }}>
                  <Input.TextArea
                    rows={2}
                    maxLength={2000}
                    value={draft}
                    placeholder="以客服身份回复（Enter 发送，Shift+Enter 换行）"
                    onChange={(event) => setDraft(event.target.value)}
                    onPressEnter={(event) => {
                      if (event.shiftKey) return;
                      event.preventDefault();
                      const content = draft.trim();
                      if (!content) return;
                      send.mutate(content);
                    }}
                  />
                  <Space style={{ marginTop: 8, width: '100%', justifyContent: 'space-between' }}>
                    <Typography.Text type="secondary">
                      首次客服回复将自动计算响应时长，超 24h 记为超时（影响上方达标率）
                    </Typography.Text>
                    <Button
                      type="primary"
                      icon={<SendOutlined />}
                      loading={send.isPending}
                      disabled={draft.trim().length === 0}
                      onClick={() => send.mutate(draft.trim())}
                    >
                      发送
                    </Button>
                  </Space>
                </div>
              </>
            )}
          </div>
        </Col>
      </Row>
    </Card>
  );
}
